import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const GOOGLE_SHEET_CONFIG = {
  proxyEndpoint: '/api/admin/google-sheets/bookings',
};

const BookingsContext = createContext(null);

export const useBookings = () => {
  const context = useContext(BookingsContext);
  if (!context) {
    throw new Error('useBookings must be used within a BookingsProvider');
  }
  return context;
};

export const formatINR = (amount) => {
  if (!amount || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export const formatINRShort = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
};

const TRACKED_FIELDS = ['amount', 'checkin', 'checkout', 'guestName', 'noOfGuests', 'roomType', 'noOfRooms', 'roomNights'];

const FIELD_LABELS = {
  amount: 'Amount',
  checkin: 'Check-In',
  checkout: 'Check-Out',
  guestName: 'Guest Name',
  noOfGuests: 'No. of Guests',
  roomType: 'Room Type',
  noOfRooms: 'No. of Rooms',
  roomNights: 'Room Nights'
};

export { FIELD_LABELS };

const parseEntry = (row) => {
  let amount = row['Amount/Net Rate'] || row['Amount'] || row['Net Rate'] || row['amount'] || 0;
  if (typeof amount === 'string') amount = parseFloat(amount.replace(/[^0-9.-]/g, '')) || 0;

  return {
    hotelName: String(row['Hotel Name'] || row['hotelName'] || row['Property'] || row['property'] || ''),
    guestName: String(row['Guest Name'] || row['guestName'] || row['Guest'] || ''),
    guestContact: String(row['Guest Contact'] || row['guestContact'] || row['Guest Phone'] || row['Contact'] || row['Phone'] || row['Mobile'] || ''),
    ota: String(row['OTA'] || row['Source'] || row['ota'] || row['source'] || ''),
    status: String(row['Status'] || row['status'] || 'pending').toLowerCase(),
    checkin: String(row['Check-In'] || row['checkin'] || row['Check In'] || row['CheckIn'] || ''),
    checkout: String(row['Check-Out'] || row['checkout'] || row['Check Out'] || row['CheckOut'] || ''),
    amount,
    city: String(row['City'] || row['city'] || ''),
    state: String(row['State'] || row['state'] || ''),
    roomType: String(row['Room Type'] || row['roomType'] || ''),
    noOfGuests: String(row['No. of Guests/Adults'] || row['noOfGuests'] || ''),
    roomNights: String(row['Room Nights'] || row['roomNights'] || ''),
    noOfRooms: String(row['No. of Rooms'] || row['noOfRooms'] || ''),
    subject: String(row['Subject'] || ''),
    propertyId: String(row['Property ID'] || '').trim(),
  };
};

const processBookingsData = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return [];

  // Step 1: Collect all entries per booking ID
  const entriesMap = new Map();

  rawData.forEach((row) => {
    const bookingId = row['Booking/Reservation ID'] || row['Booking ID'] || row['bookingId'] || row['ID'] || '';
    if (!bookingId) return;

    const dateStr = row['Booked/Email Date'] || row['Timestamp'] || row['timestamp'] || row['Created'] || row['bookingDate'] || '';
    const timestamp = dateStr ? new Date(dateStr) : new Date(0);
    if (isNaN(timestamp.getTime())) return;

    const entry = { ...parseEntry(row), timestamp, bookingDate: dateStr, rawData: row };

    if (!entriesMap.has(bookingId)) entriesMap.set(bookingId, []);
    entriesMap.get(bookingId).push(entry);
  });

  // Step 2: Process each booking - track original and changes
  const bookings = [];

  entriesMap.forEach((entries, bookingId) => {
    entries.sort((a, b) => a.timestamp - b.timestamp);

    const latest = entries[entries.length - 1];
    // First confirmed entry = original confirmed state; fallback to first entry
    const original = entries.find(e => e.status === 'confirmed') || entries[0];

    // Status-specific amounts (per Master Formula spec)
    const lastAmended = [...entries].reverse().find(e => e.status === 'amended');
    const lastCancelled = [...entries].reverse().find(e => e.status === 'cancelled');
    const confirmedAmount = original.amount;
    const amendedAmount = lastAmended ? lastAmended.amount : null;
    const cancelledAmount = lastCancelled ? lastCancelled.amount : null;

    let originalData = null;
    let changes = null;

    if (latest.status === 'cancelled' || latest.status === 'amended') {
      originalData = {};
      changes = {};
      TRACKED_FIELDS.forEach(field => {
        originalData[field] = original[field];
        if (String(original[field] ?? '') !== String(latest[field] ?? '')) {
          changes[field] = { old: original[field], new: latest[field] };
        }
      });
      originalData.status = original.status;
      originalData.bookingDate = original.bookingDate;
    }

    bookings.push({
      _id: String(bookingId),
      bookingId: String(bookingId),
      timestamp: latest.timestamp,
      bookingDate: latest.bookingDate,
      hotelName: (latest.hotelName || '').trim().replace(/\s+/g, ' '),
      guestName: latest.guestName,
      guestContact: latest.guestContact || '',
      ota: latest.ota,
      checkin: latest.checkin,
      checkout: latest.checkout,
      amount: Math.max(0, latest.amount || 0),
      status: latest.status,
      city: latest.city,
      state: latest.state,
      roomType: latest.roomType,
      noOfGuests: latest.noOfGuests,
      roomNights: latest.roomNights,
      noOfRooms: latest.noOfRooms,
      subject: latest.subject,
      propertyId: latest.propertyId || '',
      rawData: latest.rawData,
      originalAmount: Math.max(0, original.amount || 0),
      // New status-specific amounts (per Master Formula spec)
      confirmedAmount: Math.max(0, confirmedAmount || 0),
      amendedAmount: amendedAmount === null ? null : Math.max(0, amendedAmount),
      cancelledAmount: cancelledAmount === null ? null : Math.max(0, cancelledAmount),
      originalData,
      changes,
    });
  });

  return bookings.sort((a, b) => b.timestamp - a.timestamp);
};

export const BookingsProvider = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [localOverrides, setLocalOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | syncing | done | error
  const [syncStats, setSyncStats] = useState(null);
  const syncRef = useRef(null);
  const lastAutoSyncRef = useRef(0); // throttle: at most one auto-sync per 5 min
  const isFetchingRef = useRef(false); // dedupe concurrent fetches
  const hasLoadedRef = useRef(false);  // distinguish initial load from background refresh

  // Inline poll inside triggerSync to avoid circular deps
  const triggerSync = useCallback(async (processedBookings) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const startPoll = () => {
      const poll = async () => {
        try {
          const res = await fetch(`${backendUrl}/api/admin/sync-status`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!res.ok) { setSyncStatus('error'); return; }
          const d = await res.json();
          if (d.status === 'syncing') {
            setSyncStats({ ...d.stats, progress: d.progress });
            setTimeout(poll, 3000);
          } else {
            setSyncStatus(d.status === 'done' ? 'done' : 'error');
            setSyncStats(d.stats);
          }
        } catch { setSyncStatus('error'); }
      };
      setTimeout(poll, 3000);
    };

    try {
      const stripped = processedBookings.map(b => ({
        bookingId: b.bookingId, guestName: b.guestName,
        checkin: b.checkin, checkout: b.checkout,
        amount: b.amount, status: b.status, hotelName: b.hotelName,
        ota: b.ota, originalAmount: b.originalAmount,
        bookingDate: b.bookingDate,
        roomType: b.roomType, noOfGuests: b.noOfGuests,
        noOfRooms: b.noOfRooms, roomNights: b.roomNights,
        city: b.city, state: b.state, propertyId: b.propertyId,
      }));
      setSyncStatus('syncing');
      const res = await fetch(`${backendUrl}/api/admin/sync-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ bookings: stripped }),
      });
      if (!res.ok) { setSyncStatus('error'); return; }
      const data = await res.json();
      if (data.status === 'up_to_date') {
        setSyncStatus('done');
        setSyncStats({ created: 0, updated: 0, skipped: data.skipped, failed: 0 });
      } else if (data.status === 'syncing' || data.status === 'already_syncing') {
        setSyncStatus('syncing');
        startPoll();
      }
    } catch { setSyncStatus('error'); }
  }, []);

  // Store latest triggerSync in a ref so fetchBookings doesn't depend on it
  syncRef.current = triggerSync;

  const fetchBookings = useCallback(async (showToast = true, retryCount = 0) => {
    // Deduplicate concurrent fetches (background poll must not stack on a manual refresh).
    if (retryCount === 0 && isFetchingRef.current) return;
    isFetchingRef.current = true;
    // Only block the UI with a full-page spinner on the very first load.
    // Background polls refresh quietly and NEVER clear the table.
    if (!hasLoadedRef.current) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${backendUrl}${GOOGLE_SHEET_CONFIG.proxyEndpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 504 && retryCount < 2) {
          if (showToast) toast.info('Request timed out, retrying...');
          setTimeout(() => fetchBookings(showToast, retryCount + 1), 1000);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let rawBookings = [];
      if (data.status === 'success' && Array.isArray(data.data)) rawBookings = data.data;
      else if (Array.isArray(data)) rawBookings = data;
      else if (data.data && Array.isArray(data.data)) rawBookings = data.data;

      const processedBookings = processBookingsData(rawBookings);

      // Fetch local overrides from MongoDB
      let localMap = {};
      try {
        const localRes = await fetch(`${backendUrl}/api/admin/bookings/local`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (localRes.ok) {
          const localData = await localRes.json();
          (localData.bookings || []).forEach(lb => {
            localMap[lb.booking_id] = lb;
          });
        }
      } catch { /* non-blocking */ }

      // Merge: local overrides win by booking_id
      const mergedBookings = processedBookings.map(b => {
        const local = localMap[b.bookingId];
        if (!local) return b;
        delete localMap[b.bookingId]; // remove from map so we don't double-add
        return {
          ...b,
          hotelName: local.hotel_name || b.hotelName,
          guestName: local.guest_name || b.guestName,
          ota: local.ota || b.ota,
          bookingDate: local.booking_date || b.bookingDate,
          checkin: local.check_in || b.checkin,
          checkout: local.check_out || b.checkout,
          amount: local.amount ?? b.amount,
          status: local.status || b.status,
          cancellationAmount: local.cancellation_amount || 0,
          _localOverride: true,
        };
      });

      // Add purely local bookings (not in Google Sheets)
      Object.values(localMap).forEach(lb => {
        mergedBookings.push({
          _id: lb.booking_id,
          bookingId: lb.booking_id,
          hotelName: lb.hotel_name || '',
          guestName: lb.guest_name || '',
          ota: lb.ota || '',
          bookingDate: lb.booking_date || '',
          checkin: lb.check_in || '',
          checkout: lb.check_out || '',
          amount: lb.amount || 0,
          status: lb.status || 'confirmed',
          cancellationAmount: lb.cancellation_amount || 0,
          timestamp: new Date(lb.created_at || Date.now()),
          city: '', state: '', roomType: '', noOfGuests: '', roomNights: '', noOfRooms: '', subject: '',
          originalAmount: lb.amount || 0,
          originalData: null, changes: null,
          _localOverride: true,
          _localOnly: true,
        });
      });

      setLocalOverrides(localMap);
      setBookings(mergedBookings);
      setLastFetched(new Date());
      hasLoadedRef.current = true;
      if (showToast) toast.success(`Loaded ${mergedBookings.length} bookings`);

      // NOTE: Booking → Superhost App sync is now BACKEND-DRIVEN (event-driven on each
      // Google-Sheet cache refresh). The frontend no longer triggers sync, so bookings
      // reach the App even when no Admin browser is open. Manual re-sync (if needed) is
      // still available via the explicit "Sync" action.
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (retryCount < 2) {
        if (showToast) toast.info('Connection error, retrying...');
        setTimeout(() => fetchBookings(showToast, retryCount + 1), 1000);
        return;
      }
      setError(err.message);
      if (showToast) toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchBookings(false);
    const interval = setInterval(() => fetchBookings(false), 60000);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  const addBooking = useCallback(async (bookingData) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backendUrl}/api/admin/bookings/local`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(bookingData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    const lb = data.booking;
    // Add to local state immediately
    setBookings(prev => [{
      _id: lb.booking_id, bookingId: lb.booking_id,
      hotelName: lb.hotel_name, guestName: lb.guest_name, ota: lb.ota,
      bookingDate: lb.booking_date, checkin: lb.check_in, checkout: lb.check_out,
      amount: lb.amount, status: lb.status, cancellationAmount: lb.cancellation_amount,
      timestamp: new Date(lb.created_at), city: '', state: '', roomType: '',
      noOfGuests: '', roomNights: '', noOfRooms: '', subject: '',
      originalAmount: lb.amount, originalData: null, changes: null,
      _localOverride: true, _localOnly: true,
    }, ...prev]);
    toast.success('Booking created');
    return data;
  }, []);

  const updateBooking = useCallback(async (bookingId, bookingData) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backendUrl}/api/admin/bookings/local/${encodeURIComponent(bookingId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(bookingData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    const lb = data.booking;
    setBookings(prev => prev.map(b =>
      b.bookingId === bookingId ? {
        ...b,
        hotelName: lb.hotel_name, guestName: lb.guest_name, ota: lb.ota,
        bookingDate: lb.booking_date, checkin: lb.check_in, checkout: lb.check_out,
        amount: lb.amount, status: lb.status, cancellationAmount: lb.cancellation_amount,
        _localOverride: true,
      } : b
    ));
    toast.success('Booking updated');
    return data;
  }, []);

  const deleteBooking = useCallback(async (bookingId) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backendUrl}/api/admin/bookings/local/${encodeURIComponent(bookingId)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    setBookings(prev => prev.filter(b => b.bookingId !== bookingId));
    setInternalStatuses(prev => { const n = { ...prev }; delete n[bookingId]; return n; });
    toast.success('Booking deleted');
  }, []);

  // Internal booking status (separate from Google Sheet status)
  const [internalStatuses, setInternalStatuses] = useState({});

  const fetchInternalStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/bookings/internal-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInternalStatuses(data.data || {});
      }
    } catch { /* silent */ }
  }, []);

  const updateInternalStatus = useCallback(async (bookingId, newStatus) => {
    const token = localStorage.getItem('adminToken');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backendUrl}/api/admin/bookings/internal-status/${encodeURIComponent(bookingId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ internal_status: newStatus }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    setInternalStatuses(prev => ({ ...prev, [bookingId]: newStatus }));
  }, []);

  useEffect(() => { fetchInternalStatuses(); }, [fetchInternalStatuses]);

  // Read-only approval statuses: Owner Status (Superhost App) + Sneha Status (BFR).
  const [approvalStatuses, setApprovalStatuses] = useState({});

  const fetchApprovalStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/bookings/approval-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApprovalStatuses(data.data || {});
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchApprovalStatuses();
    const id = setInterval(fetchApprovalStatuses, 30000);
    return () => clearInterval(id);
  }, [fetchApprovalStatuses]);

  return (
    <BookingsContext.Provider value={{
      bookings, loading, refreshing, error, lastFetched, fetchBookings,
      syncStatus, syncStats, addBooking, updateBooking, deleteBooking,
      internalStatuses, updateInternalStatus, fetchInternalStatuses,
      approvalStatuses, fetchApprovalStatuses,
    }}>
      {children}
    </BookingsContext.Provider>
  );
};

export default BookingsContext;
